<template>
  <v-container fluid class="ncrm-integrations">
    <div class="ncrm-integrations__header" data-tour="integrations-header">
      <div class="ncrm-integrations__header-left">
        <v-icon class="ncrm-integrations__header-icon" size="16">mdi-api</v-icon>
        <h1 class="ncrm-integrations__title">{{ $t("ncrm.integrations.title") }}</h1>
        <span class="ncrm-integrations__subtitle">{{ $t("ncrm.integrations.subtitle") }}</span>
      </div>
      <div class="ncrm-integrations__header-right">
        <v-btn
          color="primary"
          variant="tonal"
          data-tour="integrations-connected-apps"
          @click="handleConnectedApps"
        >
          <v-icon start size="16">mdi-link-variant</v-icon>
          {{ $t("ncrm.integrations.connectedApps") }}
        </v-btn>
        <PageHelpButton />
      </div>
    </div>

    <v-card class="mt-4">
      <v-tabs v-model="activeTab" color="primary" data-tour="integrations-tabs">
        <v-tab value="file" :disabled="!canAccessFileImports">{{ $t("ncrm.integrations.tabs.fileImports") }}</v-tab>
        <v-tab value="external" :disabled="!canAccessExternalApi">{{ $t("ncrm.integrations.tabs.externalApi") }}</v-tab>
        <v-tab value="mailjet" :disabled="!canAccessMailjet">{{ $t("ncrm.integrations.tabs.mailjet") }}</v-tab>
        <v-tab value="smtp" :disabled="!canAccessCompanySmtp">{{ $t("ncrm.integrations.tabs.smtp") }}</v-tab>
        <v-tab value="pbx" :disabled="!canAccessPbx">{{ $t("ncrm.integrations.tabs.pbx") }}</v-tab>
        <v-tab value="telegram" :disabled="!canAccessTelegram">{{ $t("ncrm.integrations.tabs.telegram") }}</v-tab>
        <v-tab value="apps" :disabled="!canAccessAppSyncs">
          {{ $t("ncrm.integrations.tabs.appSyncs") }}
        </v-tab>
        <v-tab value="studio" disabled>{{ $t("ncrm.integrations.tabs.dataStudio") }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text>
        <!-- DPIA A4 (riscul R9): la activarea unei integrări, consecința asupra
             datelor trebuie să fie vizibilă acolo, nu doar în contract. -->
        <NcrmIntegrationPrivacyNotice v-if="activeTab" :integration-key="integrationPrivacyKey" />
        <v-window v-model="activeTab" :touch="false">
          <v-window-item value="file">
            <NcrmFileImportsTab v-if="canAccessFileImports" @notify="showSnackbar" />
          </v-window-item>

          <v-window-item value="external">
            <div v-if="canAccessExternalApi">
              <NcrmExternalApiWorkspace :initial-delivery-id="initialDeliveryId" />
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.externalApi.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="mailjet">
            <div v-if="canAccessMailjet">
              <NcrmMailjetWorkspace v-if="canManageMailjet" />
              <NcrmMailjetPrivacyWorkspace v-else />
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.mailjet.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="smtp">
            <div v-if="canAccessCompanySmtp">
              <NcrmCompanySmtpWorkspace />
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.smtp.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="pbx">
            <div v-if="canAccessPbx">
              <NcrmPbxWorkspace />
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.pbx.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="telegram">
            <div v-if="canAccessTelegram">
              <TelegramWorkspace />
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.telegram.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="apps">
            <div v-if="canAccessAppSyncs">
              <v-tabs v-model="appSyncTab" color="primary" class="mb-4">
                <v-tab value="sync">{{ $t("ncrm.integrations.appSyncs.tabs.syncJobs") }}</v-tab>
                <v-tab value="candidates">{{ $t("ncrm.integrations.appSyncs.tabs.importCandidates") }}</v-tab>
              </v-tabs>
              <v-window v-model="appSyncTab" :touch="false">
                <v-window-item value="sync">
                  <NcrmMitpSyncPanel />
                </v-window-item>
                <v-window-item value="candidates">
                  <NcrmMitpImportCandidatesPanel />
                </v-window-item>
              </v-window>
            </div>
            <v-alert v-else type="info">
              {{ $t("ncrm.integrations.appSyncs.messages.adminOnly") }}
            </v-alert>
          </v-window-item>

          <v-window-item value="studio">
            <v-alert type="info">
              {{ $t("ncrm.integrations.connectedAppsSoon") }}
            </v-alert>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar.show" :timeout="3000" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import NcrmIntegrationPrivacyNotice from "@/components/ncrm/integrations/NcrmIntegrationPrivacyNotice.vue";
import NcrmExternalApiWorkspace from "@/components/ncrm/integrations/NcrmExternalApiWorkspace.vue";
import NcrmFileImportsTab from "@/components/ncrm/integrations/NcrmFileImportsTab.vue";
import NcrmCompanySmtpWorkspace from "@/components/ncrm/integrations/NcrmCompanySmtpWorkspace.vue";
import NcrmPbxWorkspace from "@/components/ncrm/integrations/NcrmPbxWorkspace.vue";
import NcrmMailjetWorkspace from "@/components/ncrm/integrations/mailjet/NcrmMailjetWorkspace.vue";
import NcrmMailjetPrivacyWorkspace from "@/components/ncrm/integrations/mailjet/NcrmMailjetPrivacyWorkspace.vue";
import TelegramWorkspace from "@/components/integrations/telegram/TelegramWorkspace.vue";
import NcrmMitpImportCandidatesPanel from "@/components/ncrm/integrations/NcrmMitpImportCandidatesPanel.vue";
import NcrmMitpSyncPanel from "@/components/ncrm/integrations/NcrmMitpSyncPanel.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
import {
  canAccessCompanySmtpWorkspace,
  canAccessDataIntegrationsImports,
  canAccessExternalApiWorkspace,
  canAccessMailjetWorkspace,
  canManageMailjetWorkspace,
  canAccessMitpAppSyncs,
  canAccessPbxWorkspace,
  canAccessTelegramWorkspace,
} from "@/Utile/ncrmMitpSync.mjs";

export default {
  name: "NcrmDataIntegrations",
  components: {
    NcrmIntegrationPrivacyNotice,
    NcrmExternalApiWorkspace,
    NcrmFileImportsTab,
    NcrmCompanySmtpWorkspace,
    NcrmPbxWorkspace,
    NcrmMailjetWorkspace,
    NcrmMailjetPrivacyWorkspace,
    TelegramWorkspace,
    NcrmMitpImportCandidatesPanel,
    NcrmMitpSyncPanel,
    PageHelpButton,
  },
  data() {
    return {
      activeTab: "file",
      appSyncTab: "sync",
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    integrationPrivacyKey() {
      return this.activeTab === "external" ? "external_api" : this.activeTab;
    },
    canAccessFileImports() {
      return canAccessDataIntegrationsImports();
    },
    canAccessAppSyncs() {
      return canAccessMitpAppSyncs();
    },
    canAccessExternalApi() {
      return canAccessExternalApiWorkspace();
    },
    canAccessMailjet() {
      return canAccessMailjetWorkspace();
    },
    canManageMailjet() {
      return canManageMailjetWorkspace();
    },
    canAccessCompanySmtp() {
      return canAccessCompanySmtpWorkspace();
    },
    canAccessPbx() {
      return canAccessPbxWorkspace();
    },
    canAccessTelegram() {
      return canAccessTelegramWorkspace();
    },
    initialDeliveryId() {
      const raw = this.$route?.query?.deliveryId;
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
  },
  mounted() {
    if (this.$route?.query?.tab === "external" && this.canAccessExternalApi) {
      this.activeTab = "external";
      return;
    }
    if (this.$route?.query?.tab === "mailjet" && this.canAccessMailjet) {
      this.activeTab = "mailjet";
      return;
    }
    if (this.$route?.query?.tab === "smtp" && this.canAccessCompanySmtp) {
      this.activeTab = "smtp";
      return;
    }
    if (this.$route?.query?.tab === "pbx" && this.canAccessPbx) {
      this.activeTab = "pbx";
      return;
    }
    if (this.$route?.query?.tab === "telegram" && this.canAccessTelegram) {
      this.activeTab = "telegram";
      return;
    }
    if (this.canAccessFileImports) {
      return;
    }
    if (this.canAccessExternalApi) {
      this.activeTab = "external";
      return;
    }
    if (this.canAccessMailjet) {
      this.activeTab = "mailjet";
      return;
    }
    if (this.canAccessCompanySmtp) {
      this.activeTab = "smtp";
      return;
    }
    if (this.canAccessPbx) {
      this.activeTab = "pbx";
      return;
    }
    if (this.canAccessAppSyncs) {
      this.activeTab = "apps";
    }
  },
  methods: {
    showSnackbar(payload) {
      if (!payload) return;
      this.snackbar.message = payload.message || "";
      this.snackbar.color = payload.color || "info";
      this.snackbar.show = true;
    },
    handleConnectedApps() {
      if (this.canAccessAppSyncs) {
        this.activeTab = "apps";
        return;
      }
      if (this.canAccessExternalApi) {
        this.activeTab = "external";
        return;
      }
      if (this.canAccessMailjet) {
        this.activeTab = "mailjet";
        return;
      }
      if (this.canAccessCompanySmtp) {
        this.activeTab = "smtp";
        return;
      }
      if (this.canAccessPbx) {
        this.activeTab = "pbx";
        return;
      }
      this.showSnackbar({ message: this.$t("ncrm.integrations.connectedAppsSoon") });
    },
  },
};
</script>

<style scoped>
.ncrm-integrations {
  text-align: left;
}

/* ── Breadcrumb strip ─────────────────────────────────────── */
.ncrm-integrations__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 2px 0 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.ncrm-integrations__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 0;
}

.ncrm-integrations__header-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-tertiary));
  opacity: 0.85;
}

.ncrm-integrations__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-integrations__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.6;
  white-space: nowrap;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  margin-left: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-integrations__header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 960px) {
  .ncrm-integrations__header { align-items: flex-start; }
  .ncrm-integrations__header-right { width: 100%; justify-content: flex-end; }
  .ncrm-integrations__subtitle { display: none; }
}
</style>
